import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Fragment } from '@tiptap/pm/model';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    eventHandlerCommands: {
      indent: () => ReturnType;
    };
  }
}

function arrowUpInFirstRow(view: any) {
  const { top } = view.dom.getBoundingClientRect();
  const { pos } = view.state.selection.$from;
  const { top: cursorTop } = view.coordsAtPos(pos);
  const { node } = view.domAtPos(pos);
  // if node is a text node, get the parent
  const domAtPos = node.nodeName === '#text' ? node.parentElement : node;
  let currentElement = domAtPos;
  let topOffset = 2;

  // traverse up the dom tree until we reach the editor
  do {
    const { borderTop, marginTop, paddingTop } = window.getComputedStyle(currentElement);
    const margin = parseInt(marginTop, 10);
    const padding = parseInt(paddingTop, 10);
    const border = parseInt(borderTop, 10);
    topOffset += margin + padding + border;
    currentElement = currentElement.parentElement;
  } while (currentElement && currentElement !== view.dom);

  const cursorTopOffsetted = Math.floor(cursorTop) - topOffset;
  return cursorTopOffsetted <= top;
}

function arrowUpHandler(view: any, event: any, editor: any, emitter: any) {
  const { $from, $to } = view.state.selection;
  if ($from.parentOffset !== $to.parentOffset || $from.pos !== $to.pos) return;
  if (editor.storage.mention.mentionDropdownActive) return false;
  if (arrowUpInFirstRow(view)) {
    emitter.$emit('arrow-up-in-first-row', {
      // @ts-ignore
      editorId: editor.id,
      rect: view.coordsAtPos(view.state.selection.$from.pos),
    });
    return true;
  }
}

function arrowLeftHandler(view: any, event: any, editor: any, emitter: any) {
  const { $from } = view.state.selection;
  // Arrow left at the beginning of the document
  if ($from.parentOffset === 0 && $from.index(0) === 0) {
    // The cursor is at the beginning of the current node
    emitter.$emit('arrow-left-at-doc-start', {
      // @ts-ignore
      editorId: editor.id,
    });
    return true;
  }
}

function backspaceHandler(view: any, event: any, editor: any) {
  const { $from, empty, $anchor, $to } = view.state.selection;
  const node = $anchor.node();
  const range = $from.blockRange($to);
  const allMarks: any = new Set();
  const combinedKey = event.ctrlKey || event.metaKey || event.altKey;

  const extractMarks = (marks: any = []) => {
    marks.forEach((mark: any) => allMarks.add(mark));
  };

  const isTextNodeWithSingleCharLeft = (node: any) => {
    return node?.isText && node?.textContent?.length === 1;
  };

  const isNodeWithSurroundingTextNodes = (node: any) => {
    return node?.childCount === 3 && isTextNodeWithSingleCharLeft(node?.content?.content[1]);
  };

  const isNodeWithSingleCharLeft = (node: any) => {
    return (
      combinedKey ||
      isTextNodeWithSingleCharLeft(node) ||
      isNodeWithSurroundingTextNodes(node) ||
      (node?.childCount >= 1 && isTextNodeWithSingleCharLeft(node?.lastChild))
    );
  };

  if (empty && (isNodeWithSingleCharLeft(node) || isNodeWithSingleCharLeft($from.nodeBefore))) {
    if (range) {
      view.state.doc.nodesBetween($from.pos, $to.pos, (node: any) => {
        if (node.isText) {
          extractMarks(node.marks);
        } else if (node.childCount === 1 && node.firstChild?.isText) {
          extractMarks(node.firstChild.marks);
        } else if (node.content instanceof Fragment) {
          const fragment = node.content;
          // @ts-ignore
          fragment.content?.forEach((node) => {
            if (node.isText) {
              extractMarks(node.marks);
            }
          });
        }
      });
    }
    if (allMarks.size > 0) {
      allMarks.forEach((mark: any) => {
        editor.commands.toggleMark(mark.type.name);
      });
      window.requestAnimationFrame(() => {
        view.state.storedMarks = null;
      });
    }
  }
}

export interface EventHandlerOptions {
  emitter: any;
  handleArrowLeft: boolean;
}

export const EventHandler = (options: EventHandlerOptions) => {
  return Extension.create({
    name: 'eventHandler',

    addCommands() {
      return {
        indent:
          () =>
          ({ commands }) => {
            return commands.insertContent('\t');
          },
      };
    },

    addKeyboardShortcuts() {
      return {
        Tab: () => {
          // prevent indenting when mention is active
          // @ts-ignore
          if (this.editor.view.state.mention$?.active) return false;
          if (
            this.editor.isActive('bulletList') ||
            this.editor.isActive('orderedList') ||
            this.editor.isActive('taskList')
          )
            return false;
          return this.editor.commands.indent();
        },
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('eventHandler'),
          props: {
            handleKeyDown: (view, event) => {
              // Arrow up in first row
              if (event.key === 'ArrowUp') {
                return arrowUpHandler(view, event, this.editor, options.emitter);
              }
              // Arrow left
              if (event.key === 'ArrowLeft') {
                if (!options.handleArrowLeft) return false;
                return arrowLeftHandler(view, event, this.editor, options.emitter);
              }

              if (event.key === 'Backspace') {
                backspaceHandler(view, event, this.editor);
                return false;
              }
              // tab
              if (event.key === 'Tab') {
                event.preventDefault();
              }
            },
          },
        }),
      ];
    },
  });
};
